import { createRoot } from "react-dom/client";
import App from "./App";
import "./index.css";
import "./lib/i18n";
import { isChunkLoadError, reloadForFreshShell } from "./lib/pwa-update";
import { listQueue } from "./lib/offline";
import { queryClient } from "./lib/queryClient";
import { MaintenanceInstallMetadata, syncMaintenanceInstallMetadata } from "./lib/maintenance-install";

// Polyfill/fix Date.prototype.toISOString to handle corrupted prototypes
// This ensures that any Date object can be safely serialized
const originalToISOString = Date.prototype.toISOString;
Date.prototype.toISOString = function() {
  try {
    // Try the original method first
    if (originalToISOString) {
      return originalToISOString.call(this);
    }
  } catch (e) {
    // If it fails, build the string manually
  }
  
  // Manual ISO string construction as fallback
  const d = this;
  if (isNaN(d.getTime())) {
    throw new RangeError('Invalid time value');
  }
  const year = d.getUTCFullYear();
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  const hours = String(d.getUTCHours()).padStart(2, '0');
  const minutes = String(d.getUTCMinutes()).padStart(2, '0');
  const seconds = String(d.getUTCSeconds()).padStart(2, '0');
  const ms = String(d.getUTCMilliseconds()).padStart(3, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${ms}Z`;
};

// Also ensure toJSON calls our patched toISOString
Date.prototype.toJSON = function() {
  return this.toISOString();
};

// If a dynamically-imported page chunk fails to load (typically because a new
// version was published mid-session and the old hashed file is gone), refresh
// onto the latest assets instead of crashing.
if (typeof window !== "undefined") {
  // Fail closed until the durable queue has been read. A visible open form,
  // user edits or in-flight write are additionally protected by the early script.
  window.__appRecovery?.addSafetyCheck(async () =>
    queryClient.isMutating() === 0 && (await listQueue()).length === 0);
  window.addEventListener("unhandledrejection", (event) => {
    if (!isChunkLoadError(event.reason)) return;
    event.preventDefault();
    void reloadForFreshShell("unhandled-chunk");
  });
}

// Register the PWA service worker (generated by vite-plugin-pwa) and keep the
// installed shell up to date for long-lived sessions.
if (import.meta.env.PROD && "serviceWorker" in navigator) {
  // No virtual:pwa-register auto-reload: app-recovery owns all reload decisions.
  navigator.serviceWorker.register("/sw.js", { updateViaCache: "none" })
    .then(registration => {
      const activateWaiting = () => registration.waiting?.postMessage({ type: "SKIP_WAITING" });
      const watchInstalling = () => {
        registration.installing?.addEventListener("statechange", activateWaiting);
        activateWaiting();
      };
      registration.addEventListener("updatefound", watchInstalling);
      watchInstalling();
      const check = () => { void registration.update().catch(() => {}); };
      check();
      setInterval(check, 60_000);
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") check();
      });
    })
    .catch(err => console.warn("[pwa] registration failed:", err));
}

syncMaintenanceInstallMetadata(window.location.pathname);
if ((window as Window & { __browserCompatibility?: { supported: boolean } }).__browserCompatibility?.supported !== false) {
  createRoot(document.getElementById("root")!).render(<><MaintenanceInstallMetadata/><App /></>);
}
